import { api } from '@common';
import {
  CardForm,
  InputMask,
  PrimaryButton,
  RadioGender,
  WrapperPage,
} from '@components/atoms';
import { maskUSPhone } from '@helpers';
import { useAuth } from '@hooks';
import {
  DatePicker,
  Form,
  Input,
  message,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';

export const SettingsPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { profile } = useAuth();

  const [form] = Form.useForm();

  const { data, isLoading: isLoadingProfile, refetch } = profile;

  const updateProfile = (formData: any) => {
    setIsLoading(true);
    // Transform the dayjs object back to a string in YYYY-MM-DD format before sending
    const payload = {
      ...formData,
      birthday: formData.birthday ? formData.birthday.format('YYYY-MM-DD') : null,
    };
    api
      .patch('/users/profile', payload)
      .then(() => {
        message.success('Profile updated');
        refetch();
      })
      .catch(error => {
        console.error('Error updating profile:', error);
        message.error('Failed to update profile');
      })
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    if (!isLoadingProfile && data) {
      const formValues = {
        ...data?.physicalPerson,
        birthday: data?.physicalPerson?.birthday
          ? dayjs(data?.physicalPerson?.birthday)
          : null,
        phone: data?.physicalPerson?.phone ? maskUSPhone(data?.physicalPerson?.phone) : null,
      };
      form.setFieldsValue(formValues);
    }
  }, [data, isLoadingProfile, form]);

  return (
    <div className="bg-black bg-center bg-cover pt-12 pb-10 md:pb-20 min-h-screen">
      <WrapperPage isLoading={isLoading || isLoadingProfile}>
        <div className="container h-full flex flex-col justify-center items-center mx-auto">
          <div className="font-android !font-normal text-yellow-principal text-4xl md:text-5xl lg:text-6xl tracking-wider pb-12 md:pb-16 uppercase">
            Settings
          </div>
        </div>

        <CardForm className="w-full max-w-4xl mx-auto">
          <Typography.Title level={2} className='font-android !font-normal text-yellow-principal uppercase tracking-wider'>
            Personal Information
          </Typography.Title>

          <Form
            size='large'
            layout='vertical'
            form={form}
            onFinish={updateProfile}
            className='grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 mt-6'
          >
            <Form.Item 
              label={<span className="font-android text-white uppercase tracking-wider">Name</span>} 
              name='name' 
              rules={[{ required: true, message: "Please enter your name" }]}
            >
              <Input placeholder='Enter name' className="font-['Barlow']" />
            </Form.Item>

            <Form.Item 
              label={<span className="font-android text-white uppercase tracking-wider">Date of Birth</span>} 
              name='birthday'
            >
              <DatePicker
                className='w-full'
                format="MM/DD/YYYY"
                placeholder='MM/DD/YYYY'
              />
            </Form.Item>

            <Form.Item 
              label={<span className="font-android text-white uppercase tracking-wider">Phone</span>} 
              name='phone'
            >
              <InputMask mask="usphone" placeholder='(000) 000-0000' />
            </Form.Item>

            <Form.Item
              label={<span className="font-android text-white uppercase tracking-wider">Gender</span>}
              name='gender'
            >
              <RadioGender />
            </Form.Item>

            <Form.Item 
              label={<span className="font-android text-white uppercase tracking-wider">Profession</span>} 
              name='profession'
            >
              <Input placeholder='Enter profession' className="font-['Barlow']" />
            </Form.Item>

            <div className='col-span-1 md:col-span-2 flex justify-center my-4'>
              <PrimaryButton 
                htmlType='submit'
                size='large'
                className='w-full md:w-80 text-xl md:text-2xl uppercase'
              >
                UPDATE PROFILE
              </PrimaryButton>
            </div>
          </Form>
        </CardForm>
      </WrapperPage>
    </div>
  );
};
